/**
 * useContractKanbanColumn - Carrega os contratos de uma coluna do Kanban de Contratos
 *
 * Implementa cursor-based pagination (created_at + id) por coluna, seguindo o
 * mesmo padrão do useKanbanColumn.ts (CRM) e useTaskKanbanColumn.ts (Tarefas).
 * Cada coluna carrega apenas a própria página, evitando trazer a lista inteira.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-12 v1.1.0 - Cursor/ordenação por data de ALTERAÇÃO (updated_at) em vez de
 *          created_at: contratos antigos que mudam de status voltam ao topo da coluna
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ContractFilters, PaginatedContract } from '@/hooks/useContractsPaginated';
import type { ContractStatusGroup } from '@/components/contracts/kanban/contractKanbanColumns';

// Logger centralizado (padrão do projeto)
const logger = createLogger('ContractKanbanColumn');

// Tamanho da página por coluna
export const CONTRACT_KANBAN_PAGE_SIZE = 5; // v1.2.0: carga inicial reduzida (20 -> 5)

/**
 * Contrato exibido no card do Kanban.
 * Reaproveita a mesma interface da listagem, sem o campo de paginação `total_count`.
 */
export type KanbanContract = Omit<PaginatedContract, 'total_count'> & {
  /** Data da última alteração do contrato (base de ordenação do Kanban) */
  updated_at: string;
};

/** Cursor de paginação por coluna (ordenado por data de alteração) */
interface ContractKanbanCursor {
  updated_at: string;
  id: string;
}

/**
 * Hook para carregar uma coluna (grupo de status) do Kanban de Contratos.
 *
 * @param statusGroup - Grupo/coluna a carregar
 * @param filters - Filtros compartilhados com a listagem (status é ignorado aqui)
 * @param enabled - Se deve buscar dados
 */
export function useContractKanbanColumn(
  statusGroup: ContractStatusGroup,
  filters?: ContractFilters,
  enabled: boolean = true
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ['contract-kanban-column', currentTenant?.id, statusGroup, filters],
    queryFn: async ({ pageParam }) => {
      logger.debug(`Buscando contratos da coluna ${statusGroup}, cursor:`, pageParam);

      const { data, error } = await supabase.rpc('get_contracts_kanban_column', {
        p_status_group: statusGroup,
        p_limit: CONTRACT_KANBAN_PAGE_SIZE,
        p_cursor_updated_at: pageParam?.updated_at || null,
        p_cursor_id: pageParam?.id || null,
        p_workspace_id: filters?.workspace_id || null,
        p_search: filters?.search || null,
        p_seller_id: filters?.seller_id || null,
        p_assigned_to: filters?.assigned_to || null,
        p_date_field: filters?.date_field || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        p_signature_provider_slug: filters?.signature_provider_slug || null,
        p_signer_role: filters?.signer_role || null,
        p_published_contract_id: filters?.published_contract_id || null,
        p_template_version_id: filters?.template_version_id || null,
        p_pending_signer_role: filters?.pending_signer_role || null,
        p_tag_ids: filters?.tag_ids || null,
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar coluna do Kanban de Contratos:', error.message);
        throw error;
      }

      return (data || []) as KanbanContract[];
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < CONTRACT_KANBAN_PAGE_SIZE) return undefined;
      const lastItem = lastPage[lastPage.length - 1];
      return { updated_at: lastItem.updated_at, id: lastItem.id } as ContractKanbanCursor;
    },
    initialPageParam: null as ContractKanbanCursor | null,
    enabled: !!currentTenant?.id && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // Rede de segurança conservadora — o Realtime é a fonte principal de atualização
    refetchInterval: 60_000,
  });

  // Invalida somente esta coluna
  const invalidateColumn = () => {
    queryClient.invalidateQueries({
      queryKey: ['contract-kanban-column', currentTenant?.id, statusGroup],
    });
  };

  const contracts = query.data?.pages.flat() || [];

  return {
    contracts,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    invalidateColumn,
    error: query.error,
  };
}

/**
 * Hook utilitário para invalidar todas as colunas e as contagens do Kanban.
 */
export function useInvalidateContractKanban() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['contract-kanban-column'] });
    queryClient.invalidateQueries({ queryKey: ['contract-kanban-counts'] });
  };
}
